import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { safeSetItem } from "../utils/safeStorage";
import { useVideo } from "./VideoContext";
import { useSubtitleSettings } from "./SubtitleSettingsContext";

const ShadowTechniqueContext = createContext();

export const useShadowTechnique = () => useContext(ShadowTechniqueContext);

export const ShadowTechniqueProvider = ({ children }) => {
  const { videoRef, volume, playbackRate, showSubtitles } = useVideo();
  const {
    getEffectiveDisplayMode,
    DISPLAY_MODES,
    setTemporarySubtitleMode,
    clearAllTemporaryOverrides,
  } = useSubtitleSettings();

  // کلیدهای localStorage
  const STORAGE_KEY = "shadowTechniqueSettings";

  // بارگذاری تنظیمات از localStorage
  const loadSettings = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error("Error loading shadow technique settings:", error);
    }
    return null;
  };

  const savedSettings = loadSettings();

  // وضعیت اصلی تکنیک سایه
  const [isActive, setIsActive] = useState(false);
  const [showProcessBox, setShowProcessBox] = useState(
    savedSettings?.showProcessBox ?? false,
  );
  const [repeatCount, setRepeatCount] = useState(
    savedSettings?.repeatCount ?? 3,
  );
  const [currentRepeat, setCurrentRepeat] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(0);
  const [isSettingStart, setIsSettingStart] = useState(false);
  const [isSettingEnd, setIsSettingEnd] = useState(false);

  // تنظیمات هر تکرار
  const [repeatSettings, setRepeatSettings] = useState(
    savedSettings?.repeatSettings ?? [],
  );

  // مقادیر اصلی قبل از شروع تکنیک
  const originalValuesRef = useRef({
    volume: volume,
    playbackRate: playbackRate,
    subtitleMode: showSubtitles ? "show" : "hidden",
  });

  // زیرنویس وضعیت‌ها: 'show', 'hidden', 'undercover'
  const subtitleModes = ["show", "hidden", "undercover"];

  // ذخیره در localStorage هر وقت تنظیمات تغییر کرد
  useEffect(() => {
    const settings = {
      showProcessBox,
      repeatCount,
      repeatSettings,
    };
    safeSetItem(STORAGE_KEY, JSON.stringify(settings));
  }, [showProcessBox, repeatCount, repeatSettings]);

  // مقداردهی اولیه تنظیمات تکرارها
  const initializeRepeatSettings = useCallback((count) => {
    setRepeatSettings((prev) => {
      // اگه قبلاً تنظیماتی داشتیم، اونها رو حفظ کن
      if (prev.length > 0) {
        const newSettings = [];
        for (let i = 0; i < count; i++) {
          if (i < prev.length) {
            newSettings.push(prev[i]);
          } else {
            newSettings.push({
              volume: 1,
              speed: 1,
              subtitleMode: "show",
            });
          }
        }
        return newSettings;
      }

      // والا جدید بساز
      const settings = [];
      for (let i = 0; i < count; i++) {
        settings.push({
          volume: 1,
          speed: 1,
          subtitleMode: "show",
        });
      }
      return settings;
    });
  }, []);

  // به‌روزرسانی تنظیمات یک تکرار خاص
  const updateRepeatSetting = useCallback((index, field, value) => {
    setRepeatSettings((prev) => {
      const newSettings = [...prev];
      newSettings[index] = { ...newSettings[index], [field]: value };
      return newSettings;
    });
  }, []);

  // شروع تکنیک سایه
  // در startShadowTechnique:
  const startShadowTechnique = useCallback(() => {
    if (!videoRef.current || startTime >= endTime) return;

    // ذخیره مقادیر اصلی
    originalValuesRef.current = {
      volume: volume,
      playbackRate: playbackRate,
      subtitle1Mode: getEffectiveDisplayMode(1),
      subtitle2Mode: getEffectiveDisplayMode(2),
    };

    // ویدیو را به زمان شروع ببر
    videoRef.current.currentTime = startTime;
    setCurrentRepeat(1);
    setIsActive(true);

    // اعمال تنظیمات اولین تکرار
    if (repeatSettings[0]) {
      videoRef.current.volume = repeatSettings[0].volume;
      videoRef.current.playbackRate = repeatSettings[0].speed;

      // اعمال حالت زیرنویس برای هر دو زیرنویس
      if (repeatSettings[0].subtitleMode) {
        setTemporarySubtitleMode(1, repeatSettings[0].subtitleMode);
        setTemporarySubtitleMode(2, repeatSettings[0].subtitleMode);
      }
    }
  }, [
    videoRef,
    startTime,
    endTime,
    volume,
    playbackRate,
    repeatSettings,
    getEffectiveDisplayMode,
    setTemporarySubtitleMode,
  ]);

  // پایان تکنیک سایه
  const stopShadowTechnique = useCallback(() => {
    setIsActive(false);
    setCurrentRepeat(0);

    // برگشت به مقادیر اصلی
    if (videoRef.current) {
      videoRef.current.volume = originalValuesRef.current.volume;
      videoRef.current.playbackRate = originalValuesRef.current.playbackRate;
      // پاک کردن همه overrideهای موقت
      clearAllTemporaryOverrides();
    }
  }, [videoRef, clearAllTemporaryOverrides]);

  // بررسی برای تکرار بعدی
  const checkForNextRepeat = useCallback(
    (currentTime) => {
      if (!isActive || !videoRef.current) return;

      if (currentTime >= endTime) {
        if (currentRepeat < repeatCount) {
          // شروع تکرار بعدی
          videoRef.current.currentTime = startTime;
          setCurrentRepeat((prev) => prev + 1);

          // اعمال تنظیمات تکرار جدید
          if (repeatSettings[currentRepeat]) {
            videoRef.current.volume = repeatSettings[currentRepeat].volume;
            videoRef.current.playbackRate = repeatSettings[currentRepeat].speed;

            // اعمال حالت زیرنویس برای تکرار جدید
            if (repeatSettings[currentRepeat].subtitleMode) {
              setTemporarySubtitleMode(
                1,
                repeatSettings[currentRepeat].subtitleMode,
              );
              setTemporarySubtitleMode(
                2,
                repeatSettings[currentRepeat].subtitleMode,
              );
            }
          }
        } else {
          stopShadowTechnique();
        }
      }
    },
    [
      isActive,
      currentRepeat,
      repeatCount,
      startTime,
      endTime,
      repeatSettings,
      videoRef,
      stopShadowTechnique,
      setTemporarySubtitleMode,
    ],
  );

  const value = {
    // وضعیت‌ها
    isActive,
    showProcessBox,
    repeatCount,
    currentRepeat,
    startTime,
    endTime,
    isSettingStart,
    isSettingEnd,
    repeatSettings,
    subtitleModes,

    // Setters
    setIsActive,
    setShowProcessBox,
    setRepeatCount,
    setCurrentRepeat,
    setStartTime,
    setEndTime,
    setIsSettingStart,
    setIsSettingEnd,
    setRepeatSettings,

    // توابع
    initializeRepeatSettings,
    updateRepeatSetting,
    startShadowTechnique,
    stopShadowTechnique,
    checkForNextRepeat,
  };

  return (
    <ShadowTechniqueContext.Provider value={value}>
      {children}
    </ShadowTechniqueContext.Provider>
  );
};
